import { useEffect, useMemo, useState } from 'react';
import { Box, Card, Link, Text } from '@nimbus-ds/components';
import { Link as LinkRoute } from 'react-router-dom';
import { OnboardingTask, OnboardingTasksResponseDto } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { NUVEM_CHAT } from 'App/featuresFlags';
import useHasTags from 'App/hooks/useHasTags';
import { trackingSaleChannelRecommendationClick } from 'App/tracking';
import { useResponsive } from 'commons/components';
import { useStorage } from 'commons/hooks';
import { useGetCountry, useGetStoreInfo } from 'domains/Auth/hooks';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import dashboardService from 'domains/Dashboard/services';
import { getSaleChannelImg } from './img/getSaleChannelImg';
import './SalesChannelsRecommendation.scss';

interface SalesChannelsRecommendationProps {
  tasks: OnboardingTasksResponseDto['recommendationChannelTasks'];
  hasIncompleteOnboarding: boolean;
}

const ONBOARDING_NUVEM_CHAT_TASK = 'onboarding-nuvem-chat-task';

export function SalesChannelsRecommendation({
  tasks,
  hasIncompleteOnboarding,
}: Readonly<SalesChannelsRecommendationProps>) {
  const t = useTranslationDashboard();
  const country = useGetCountry();
  const { isMobile } = useResponsive();
  const [visibleTasks, setVisibleTasks] = useState<
    OnboardingTask[] | undefined
  >(tasks);
  const [hasNuvemChat] = useHasTags(NUVEM_CHAT);
  const showNuvemChatCard =
    (hasNuvemChat || country === Country.BR) && hasIncompleteOnboarding;
  const { id } = useGetStoreInfo();
  const localStorageNuvemChat = `${ONBOARDING_NUVEM_CHAT_TASK}-${id}`;
  const [nuvemChatCompleted, setNuvemChatCompleted] = useStorage<string>(
    localStorageNuvemChat,
    '',
  );

  const displayNuvemChatCard =
    showNuvemChatCard && nuvemChatCompleted !== 'completed';

  const computedTasks = useMemo<OnboardingTask[] | undefined>(() => {
    if (displayNuvemChatCard) {
      return [
        {
          name: 'chat_config',
          title: t('recommendation.chat_config_d2c.title'),
          description: t('recommendation.chat_config_d2c.description'),
          ctaText: t('recommendation.chat_config_d2c.ctaText'),
          ctaLink: '/chat',
          ctaExternal: false,
          completed: false,
          onCtaLinkClickComplete: true,
        },
      ];
    }
    return tasks;
  }, [tasks, displayNuvemChatCard, t]);

  useEffect(() => {
    setVisibleTasks(computedTasks);
  }, [computedTasks]);

  const handleAction = async (task: OnboardingTask) => {
    trackingSaleChannelRecommendationClick(task.name);

    if (task?.onCtaLinkClickComplete) {
      if (displayNuvemChatCard) {
        setNuvemChatCompleted('completed');
      }
      setVisibleTasks((prevTasks) =>
        prevTasks?.filter((t) => t.name !== task.name),
      );
      await dashboardService.updateOnboardingTaskStatus({
        task: task.name,
        status: 'completed',
      });
    }
  };

  if (!visibleTasks || visibleTasks.length === 0) return null;

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="stretch"
      justifyContent="space-between"
      gap="4"
      flex="1"
      flexWrap="wrap"
    >
      {visibleTasks.map((saleChannel) => (
        <div className="stratus--sale-channel-card" key={saleChannel.name}>
          <Card padding="none">
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                flexDirection="column"
                gap="1"
                alignSelf="center"
                padding="4"
                flex="1"
              >
                <Text color="neutral-textHigh" fontWeight="medium">
                  {saleChannel.title}
                </Text>
                <Text
                  fontSize="caption"
                  color="neutral-textLow"
                  lineHeight="caption"
                >
                  {saleChannel.description}
                </Text>
                <Link
                  textDecoration="none"
                  appearance="primary"
                  fontSize="caption"
                  lineHeight="caption"
                  as={LinkRoute}
                  onClick={() => handleAction(saleChannel)}
                  to={saleChannel.ctaLink || '#'}
                >
                  {saleChannel.ctaText}
                </Link>
              </Box>
              <img
                src={getSaleChannelImg(
                  saleChannel.name,
                  country,
                  visibleTasks.length === 1 && !isMobile,
                  displayNuvemChatCard,
                )}
                alt={`${saleChannel.name}-img`}
                height={132}
              />
            </Box>
          </Card>
        </div>
      ))}
    </Box>
  );
}
